import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import { firstValueFrom, Observable, of } from 'rxjs';
import { AuthService } from '@mogenius/user/services/auth/auth.service';
import { UserService } from '@mogenius/user/services/user/user.service';
import { concatMap, filter } from 'rxjs/operators';
import { environment } from '@mogenius/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class EmailValidGuard implements CanLoad, CanActivate, CanActivateChild {
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _userService = inject(UserService);


  public canLoad(route: Route, segments: UrlSegment[]): Promise<boolean> {
    return this.isAuthorized();
  }

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.isAuthorized();
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.isAuthorized();
  }

  private isAuthorized(segments?: UrlSegment[]): Promise<boolean> {
    if (this._authService.isReady$.getValue()) {
      return firstValueFrom(this.redirect());
    }
    return new Promise((resolve, reject) => {
      const sub = this._authService.isReady$
        .pipe(
          filter((item: boolean) => item),
          concatMap(() => this.redirect(segments))
        )
        .subscribe((data: boolean) => {
          resolve(data);
          if (sub) {
            sub.unsubscribe();
          }
        });
    });
  }

  private redirect(segments?: UrlSegment[]): Observable<boolean> {
    if (!environment.emailValidationEnabled || !!this._userService.user$.getValue()?.emailValidatedAt) {
      return of(true);
    }
    return of(false).pipe(concatMap(() => this._router.navigate(['/', 'user', 'verify-email'])));
  }
}
